import styled from '@emotion/styled';
import { useTranslation, Trans } from 'react-i18next';
import { tokens, Text, Link } from '@hotjar/hotsauce';
import type { FeedbackWidget } from '@hotjar/webapp/domains/activation/feedback-widgets/types';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { SentimentIcon } from './SentimentIcon';
import { SentimentSpread } from './SentimentSpread';
import { getSentimentAvg } from './getSentimentAvg';

const Wrapper = styled.div`
  padding: ${tokens.spacer.x2} ${tokens.spacer.x2} ${tokens.spacer.x1pt5};
  border-bottom: 1px solid ${tokens.color.ui_border_light};

  &:last-of-type {
    border-bottom: none;
  }
`;

const Summary = styled.div<{ hasResponses: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: ${tokens.color.neutral_100};
  border-radius: ${tokens.radius.l};
  ${tokens.text('body2')};
`;

const SentimentAvg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-left: 1px solid ${tokens.color.ui_border_light};
  padding: ${tokens.spacer.x2} ${tokens.spacer.x4};
`;

const IconStyled = styled(SentimentIcon)`
  margin-bottom: ${tokens.spacer.x0};
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${tokens.spacer.x3};
`;

const Subtitle = styled(Text)`
  margin-top: ${tokens.spacer.x0};
`;

interface ActiveWidgetProps {
  siteId: number;
  widgetPosition: number;
  widget: FeedbackWidget;
}

const TRACKING_UI_POSITION = 'Space Overview Feedback Card';

export function ActiveWidget({ siteId, widget, widgetPosition }: ActiveWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const hasResponses = widget.responseCount > 0;
  const sentimentAvg = widget.sentiment
    ? getSentimentAvg(widget.sentiment, widget.responseCount)
    : 0;
  const displayAvg = sentimentAvg === 0 ? '-' : sentimentAvg.toString();
  const subtitle = hasResponses
    ? t('activation:dashboard.feedback-card.total-responses', {
        count: widget.responseCount,
      })
    : t('activation:dashboard.feedback-card.waiting-for-feedback');

  const handleOnWidgetTitleClick = () => {
    trackClick(`Active Widget ${widgetPosition} Title`, TRACKING_UI_POSITION, {});
  };

  const handleOnWidgetSentimentClick = (score: number) => {
    trackClick(`Active Widget ${widgetPosition} Score ${score}`, TRACKING_UI_POSITION, {});
  };

  return (
    <Wrapper>
      <Summary hasResponses={hasResponses}>
        <Description>
          <Link
            onClick={handleOnWidgetTitleClick}
            kind="inline"
            href={`/sites/${siteId}/feedback/responses/${widget.id}`}
          >
            {widget.name}
          </Link>
          <Subtitle textType="caption" textColor="text_light">
            {subtitle}
          </Subtitle>
        </Description>
        <SentimentAvg>
          <IconStyled average={sentimentAvg} />
          <Text as="span" textColor="text_default" textType="body2">
            <Trans
              i18nKey="activation:dashboard.feedback-card.avg-score"
              values={{ score: displayAvg }}
            >
              <strong>{displayAvg}</strong> out of 5
            </Trans>
          </Text>
        </SentimentAvg>
      </Summary>
      {hasResponses && !!widget.sentiment && (
        <SentimentSpread
          onSentimentClick={handleOnWidgetSentimentClick}
          siteId={siteId}
          widgetId={widget.id}
          responseTotal={widget.responseCount}
          sentiment={widget.sentiment}
        />
      )}
    </Wrapper>
  );
}
